import { useState } from 'react'
import { PartThumbnail } from '../../components/PartThumbnail'
import { CalendarOutlined, IconShipAsReady, IconShipTogether } from '../../components/icons'
import { Chip } from '../../components/ui/Chip'
import { RadioCard } from '../../components/ui/RadioCard'
import { ChevronDownGlyph } from '../../components/ui/lucide-glyphs'
import type { components } from '../../lib/api/schema.gen'
import { amountFromCents, centsFromAmount, formatMoney } from '../cart/cart-money'
import { type ShipmentGroup, deriveShipmentGroups } from '../cart/ship-as-ready-breakdown'
import { deliverPartCount, deriveDeliveryPlanDates } from './checkout-domain'
import type { LineItem, ShipMode, ShipPlan } from './checkout-domain'

type CartLinePresentation = components['schemas']['CartLinePresentation']

export interface DeliveryPlanStepProps {
  readonly lines: readonly LineItem[]
  readonly presentationsBySourceableId?: ReadonlyMap<string, CartLinePresentation>
  /** Real, Library-resolved thumbnail URLs (`CartViewWithLineSpecifications
   * .lineThumbnails`, joined by sourceable id) — same source and shape as
   * Cart's own `CartPage.tsx`/`PartThumbnail.tsx`. Absent/missing entries
   * fall back to the decorative placeholder box, never a broken image. */
  readonly thumbnailsBySourceableId?: ReadonlyMap<string, string | null | undefined>
  readonly shipPlan: ShipPlan | undefined
  readonly onSelectMode: (mode: ShipMode) => void
  readonly busy?: boolean
  /** The checkout's current, server-derived shipping cost (§2's selected
   * method) — used as the "ship together" baseline both cards compare
   * against. */
  readonly shippingAmount: string
  /** The REAL "ship as ready" shipping cost — mirrors
   * `checkout-derivation.ts`'s `resolveCheckoutShippingAmount` exactly
   * (only a standard/catalog shipment carries priced Ground freight; else
   * $0), computed by the caller from `checkoutView.shippingMethods`'
   * `ship-ground` rate. NOT derived here: this step has no access to the
   * rate table, and a client-invented guess previously disagreed with the
   * real number the Order Summary sidebar shows once this card is actually
   * selected (UID-617). */
  readonly readyShippingAmount: string
  readonly currency: string
}

/** Design (Fictiv Platform.dc.html `g.few`/`g.many`): a shipment with a
 * handful of parts lists their names; beyond this it switches to a bare
 * count + thumbnail strip instead of an unbounded name list. */
const FEW_PARTS_THRESHOLD = 3

/** Design's `g.thumbs`/`g.moreThumbs`: only the first few thumbnails render
 * inline, with a "+N" tile for the rest — never an unbounded row. */
const THUMBNAIL_CAP = 6

function partNamesLabel(
  leg: ShipmentGroup,
  presentationsBySourceableId: ReadonlyMap<string, CartLinePresentation> | undefined,
): string {
  const names = leg.lines.map(
    (line) => presentationsBySourceableId?.get(line.sourceable.id)?.displayName,
  )
  // If ANY line's displayName is unresolved (e.g. not in the demo fixture
  // catalog), fall back to a whole-bucket aggregate ("N Part(s) · M units",
  // matching the "many"-branch's aggregate shape) rather than a per-line mix
  // of real names and quantity guesses (e.g. "Gasket Seal, custom, 2 parts"),
  // which reads as a broken name list rather than a deliberate summary.
  if (names.some((name) => !name)) {
    return `${leg.lineCount} Part${leg.lineCount === 1 ? '' : 's'} · ${leg.unitCount} unit${leg.unitCount === 1 ? '' : 's'}`
  }
  return names.filter((name): name is string => name != null).join(', ')
}

function formatShortDate(iso: string | undefined): string {
  if (!iso) return '—'
  const parsed = new Date(`${iso}T00:00`)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * §2 (conditionally §2, else absorbed into §3's numbering — see
 * `CheckoutPage`) of the single-page checkout — "How should these parts be
 * delivered?" Rendered ONLY for an RFQ-originated, multi-lead-time cart
 * (`resolveShowDeliveryPlan`). Two mutually-exclusive cards, each a
 * `RadioCard`, styled identically to every other checkout radio group
 * (§1/§2/§3) — this is a deliberate, scoped exception to ADR-0106's "no
 * checkout toggle" rule (see the domain module's comment): it writes
 * through the SAME cart-owned `ShipPlan.mode` field the Cart screen's
 * `ShipPlanCard` already drives (`PUT /cart/ship-plan`), so picking a plan
 * here keeps the Cart screen in sync automatically on "Back to cart" — no
 * bespoke sync code.
 */
export function DeliveryPlanStep({
  lines,
  presentationsBySourceableId,
  thumbnailsBySourceableId,
  shipPlan,
  onSelectMode,
  busy = false,
  shippingAmount,
  readyShippingAmount,
  currency,
}: DeliveryPlanStepProps) {
  const [schedOpen, setSchedOpen] = useState(false)

  const mode: ShipMode = shipPlan?.mode ?? 'ship_together'
  const partCount = deliverPartCount(lines)
  const { togetherDate, readyFirstDate, readyLastDate } = deriveDeliveryPlanDates(lines)
  // UID-617 — the step now shows for ANY freshly RFQ-originated cart (product
  // directive: "in any case"), not just a multi-lead-time one, so the subheading
  // must not always claim "different lead times" — that would be false for a
  // single-lead-time RFQ.
  const hasSingleLeadTime = readyFirstDate !== undefined && readyFirstDate === readyLastDate
  const subheading = hasSingleLeadTime
    ? `This quote has ${partCount} part${partCount === 1 ? '' : 's'} — choose how they ship.`
    : `This quote has ${partCount} parts with different lead times — choose how they ship.`

  const legs =
    presentationsBySourceableId !== undefined
      ? deriveShipmentGroups(lines, presentationsBySourceableId)
      : []

  // Mirrors ShipPlanCard.tsx's `shipAsReadyIsSingleShipment` exactly: "Ship as ready"
  // is meaningless — and disabled — when the cart resolves to only ONE shipment
  // group anyway (nothing to stage across multiple shipments). The readiness-bucket
  // GROUP count is the real signal, not the line count (two catalog lines share one
  // bucket) and not whether the ready-mode freight happens to price at $0 (a custom-
  // only cart's freight is quote-required, not "free" — that alone is never grounds
  // to disable).
  const shipAsReadyIsSingleShipment = presentationsBySourceableId !== undefined && legs.length <= 1
  const shipAsReadyDisabled = busy || shipAsReadyIsSingleShipment

  const togetherCostLabel = `One shipment · ${formatMoney(shippingAmount, currency)} shipping`

  const shipmentCount = Math.max(legs.length, 1)
  const readyCostLabel = `${shipmentCount} shipment${shipmentCount === 1 ? '' : 's'} · ${formatMoney(
    readyShippingAmount,
    currency,
  )} shipping`
  // The REAL delta (readyShippingAmount is the exact resolveCheckoutShippingAmount
  // result, not a guess) — can be negative (custom-only carts pay $0 ship_as_ready
  // freight, per that same formula, cheaper than ship_together's method rate).
  const deltaCents = centsFromAmount(readyShippingAmount) - centsFromAmount(shippingAmount)
  const readyTradeoffLabel =
    deltaCents === 0
      ? 'Same shipping cost as together'
      : deltaCents > 0
        ? `+${formatMoney(amountFromCents(deltaCents), currency)} shipping vs. together`
        : `−${formatMoney(amountFromCents(Math.abs(deltaCents)), currency)} shipping vs. together`
  // A savings (ready cheaper than together) is good news for the buyer —
  // green, not the "warning" orange that only fits an ACTUAL extra cost.
  const readyTradeoffTone = deltaCents > 0 ? 'warning' : deltaCents < 0 ? 'success' : 'neutral'

  return (
    <fieldset className="delivery-plan-fieldset" disabled={busy}>
      <legend className="sr-only">How should these parts be delivered?</legend>
      <p className="caption-text delivery-plan-subheading">{subheading}</p>
      <div className="delivery-plan-cards">
        <RadioCard
          name="delivery-plan-mode"
          value="ship_together"
          checked={mode === 'ship_together'}
          disabled={busy}
          onChange={() => onSelectMode('ship_together')}
          className="delivery-plan-card"
          label={
            <span className="delivery-plan-card-title">
              <IconShipTogether />
              Ship everything together
            </span>
          }
          description={
            <>
              <p className="delivery-plan-card-body">
                One shipment once every part is finished. Simpler receiving, one delivery.
              </p>
              <div className="delivery-plan-card-footer">
                <span className="delivery-plan-date-row">
                  <CalendarOutlined aria-hidden="true" />
                  Ships together ~ {formatShortDate(togetherDate)}
                </span>
                <Chip tone="brand">Lowest shipping cost</Chip>
              </div>
              <p className="caption-text delivery-plan-cost-label">{togetherCostLabel}</p>
            </>
          }
        />
        <RadioCard
          name="delivery-plan-mode"
          value="ship_as_ready"
          checked={mode === 'ship_as_ready'}
          disabled={shipAsReadyDisabled}
          aria-disabled={shipAsReadyDisabled}
          onChange={() => onSelectMode('ship_as_ready')}
          className="delivery-plan-card"
          label={
            <span className="delivery-plan-card-title">
              <IconShipAsReady />
              Ship as ready
              {shipAsReadyIsSingleShipment && <span> — single delivery</span>}
            </span>
          }
          description={
            <>
              <p className="delivery-plan-card-body">
                Parts ship the moment they finish, so early parts arrive sooner. May be multiple
                shipments.
              </p>
              <div className="delivery-plan-card-footer">
                <span className="delivery-plan-date-row">
                  <CalendarOutlined aria-hidden="true" />
                  First ~ {formatShortDate(readyFirstDate)} · last ~{' '}
                  {formatShortDate(readyLastDate)}
                </span>
                {legs.length > 0 && (
                  <div className="delivery-plan-schedule">
                    <button
                      type="button"
                      className="delivery-plan-schedule-toggle"
                      onClick={() => setSchedOpen((open) => !open)}
                      aria-expanded={schedOpen}
                    >
                      {schedOpen ? 'Hide' : 'Show'} schedule · {legs.length} shipment
                      {legs.length === 1 ? '' : 's'}
                      {/* UID-617 — one glyph that rotates via CSS, not a
                       * DownOutlined/UpOutlined swap (mirrors RequestDetailsSection's
                       * ChevronDownGlyph precedent, ADR-0116): two swapped icons share
                       * no node and so can never animate. */}
                      <span
                        className={`delivery-plan-schedule-chevron${schedOpen ? ' delivery-plan-schedule-chevron-open' : ''}`}
                        aria-hidden="true"
                      >
                        <ChevronDownGlyph />
                      </span>
                    </button>
                    {schedOpen && (
                      <ul className="delivery-plan-schedule-list">
                        {legs.map((leg, index) => {
                          const few = leg.lineCount <= FEW_PARTS_THRESHOLD
                          const shownLines = leg.lines.slice(0, THUMBNAIL_CAP)
                          const overflowCount = leg.lines.length - shownLines.length
                          return (
                            <li key={leg.bucket} className="delivery-plan-schedule-row">
                              <span className="delivery-plan-schedule-num" aria-hidden="true">
                                {index + 1}
                              </span>
                              <span className="delivery-plan-schedule-detail">
                                <span className="delivery-plan-schedule-arrives">
                                  Arrives {leg.arriveLabel}
                                </span>
                                <span className="caption-text">
                                  {leg.shipmentLabel} of {legs.length}
                                  {few
                                    ? ` · ${partNamesLabel(leg, presentationsBySourceableId)}`
                                    : ` · ${leg.lineCount} parts · ${leg.unitCount} units`}
                                </span>
                                {!few && (
                                  <span className="delivery-plan-schedule-thumbs">
                                    {shownLines.map((line) => (
                                      <PartThumbnail
                                        key={line.sourceable.id}
                                        url={
                                          thumbnailsBySourceableId?.get(line.sourceable.id) ??
                                          undefined
                                        }
                                        imgClassName="delivery-plan-schedule-thumb-img"
                                        placeholder={
                                          <span
                                            className="delivery-plan-schedule-thumb-placeholder"
                                            aria-hidden="true"
                                          />
                                        }
                                      />
                                    ))}
                                    {overflowCount > 0 && (
                                      <span
                                        className="delivery-plan-schedule-thumbs-more"
                                        aria-hidden="true"
                                      >
                                        +{overflowCount}
                                      </span>
                                    )}
                                  </span>
                                )}
                              </span>
                              <span className="delivery-plan-schedule-cost">
                                {leg.subtotalLabel}
                              </span>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                )}
                <Chip tone={readyTradeoffTone}>{readyTradeoffLabel}</Chip>
              </div>
              <p className="caption-text delivery-plan-cost-label">{readyCostLabel}</p>
            </>
          }
        />
      </div>
    </fieldset>
  )
}
